import { useCallback, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

type TzEntry = { value: string; label: string; keywords: string };

const COMMON_TIMEZONES: TzEntry[] = [
  { value: "America/St_Johns", label: "Newfoundland (NST/NDT)", keywords: "newfoundland st johns canada nl" },
  { value: "America/Halifax", label: "Atlantic (AST/ADT)", keywords: "atlantic halifax nova scotia new brunswick pei canada ns nb pe" },
  { value: "America/Toronto", label: "Eastern (EST/EDT)", keywords: "eastern toronto ontario quebec montreal canada on qc" },
  { value: "America/Winnipeg", label: "Central (CST/CDT)", keywords: "central winnipeg manitoba saskatchewan canada mb sk" },
  { value: "America/Edmonton", label: "Mountain (MST/MDT)", keywords: "mountain edmonton alberta canada ab" },
  { value: "America/Vancouver", label: "Pacific (PST/PDT)", keywords: "pacific vancouver british columbia canada bc" },
  { value: "America/Whitehorse", label: "Yukon (YT)", keywords: "yukon whitehorse canada yt" },
  { value: "America/Dawson_Creek", label: "Mountain Standard (no DST)", keywords: "dawson creek peace river canada bc" },
  { value: "America/New_York", label: "US Eastern (EST/EDT)", keywords: "eastern new york us usa" },
  { value: "America/Chicago", label: "US Central (CST/CDT)", keywords: "central chicago us usa" },
  { value: "America/Denver", label: "US Mountain (MST/MDT)", keywords: "mountain denver us usa" },
  { value: "America/Los_Angeles", label: "US Pacific (PST/PDT)", keywords: "pacific los angeles us usa" },
  { value: "America/Anchorage", label: "Alaska (AKST/AKDT)", keywords: "alaska anchorage us usa" },
  { value: "Pacific/Honolulu", label: "Hawaii (HST)", keywords: "hawaii honolulu us usa" },
  { value: "Europe/London", label: "UK (GMT/BST)", keywords: "london uk united kingdom england gmt bst" },
  { value: "Europe/Paris", label: "Central European (CET/CEST)", keywords: "paris france germany europe central" },
  { value: "Australia/Sydney", label: "Australia Eastern (AEST/AEDT)", keywords: "sydney australia eastern" },
  { value: "Asia/Tokyo", label: "Japan (JST)", keywords: "tokyo japan jst" },
  { value: "UTC", label: "UTC", keywords: "utc coordinated universal" },
];

const ALL_TIMEZONES: TzEntry[] = (() => {
  const commonValues = new Set(COMMON_TIMEZONES.map((t) => t.value));
  let rest: TzEntry[] = [];
  try {
    rest = Intl.supportedValuesOf("timeZone")
      .filter((tz) => !commonValues.has(tz))
      .map((tz) => ({ value: tz, label: tz, keywords: tz.toLowerCase() }));
  } catch {
    // fallback: just use the common list
  }
  return [...COMMON_TIMEZONES, ...rest];
})();

type TimezoneInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
};

export function TimezoneInput({ value, onChange, placeholder, className }: TimezoneInputProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayValue = search !== "" ? search : value;

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase();
    if (!q) return COMMON_TIMEZONES;
    return ALL_TIMEZONES.filter(
      (tz) =>
        tz.value.toLowerCase().includes(q) ||
        tz.label.toLowerCase().includes(q) ||
        tz.keywords.includes(q),
    ).slice(0, 15);
  }, [search]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setOpen(true);
  }, []);

  const handleSelect = useCallback(
    (tz: string) => {
      onChange(tz);
      setSearch("");
      setOpen(false);
      inputRef.current?.blur();
    },
    [onChange],
  );

  const handleFocus = useCallback(() => {
    setOpen(true);
  }, []);

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setSearch("");
        setOpen(false);
      }
    }, 150);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <Input
        ref={inputRef}
        autoComplete="off"
        placeholder={placeholder}
        className={className}
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      {open && filtered.length > 0 && (
        <ul className="absolute z-50 mt-1 max-h-52 w-full overflow-auto rounded-md border border-border bg-white py-1 text-sm shadow-lg">
          {filtered.map((tz) => (
            <li
              key={tz.value}
              className={`cursor-pointer px-3 py-1.5 hover:bg-blue-50 ${tz.value === value ? "bg-blue-50" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(tz.value);
              }}
            >
              <span className="font-medium text-gray-900">{tz.label}</span>
              {tz.label !== tz.value && (
                <span className="ml-2 text-xs text-gray-500">{tz.value}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
